import type { Dispatch } from "redux";
import type { Selection } from "../../../SelectableSvg";
import { arrayIntersection, getMiddleElement } from "../../../../lib/Util";
import { ScrollHandler } from "../EventListeners/ScrollHandler";
import {
    zoomGanttChartToHorizontalSelection,
    type GanttChartSourceTypes,
    type UpdatePlanningTableWidthAction,
} from "../reducers/properties";
import { TOGGLE_MULTISELECT_ORDERS, CLEAR_MULTISELECT_ORDERS } from "../reducers/planningTable";
import type { ToggleMultiselectOrdersAction, ClearMultiselectOrdersAction } from "../reducers/planningTable";
import type { LineOrders } from "../reducers/linesOrders";

const SCALE_FACTOR = 0.75;

const ORDER_UUID_ATTR = "order-uuid";
const LINE_UUID_ATTR = "line-uuid";

/** Matches {@link SelectableSvg} `target_selector` for Gantt order rectangles. */
export const GANTT_ORDER_RECT_TARGET_SELECTOR = "[name='order-rect']";

/**
 * Restrict selection to orders belonging to a single line.
 * `first_selected_el` is the initial order element picked during rectangle selection; its line is
 * the reference. The line UUID is read from the nearest parent carrying `line-uuid`.
 */
export const normalizeSelectedLineOrderUuids = (
    first_selected_el: Element,
    order_uuids: string[],
    lines_orders: LineOrders[]
): string[] => {
    const selected_line_uuid = first_selected_el.closest(`[${LINE_UUID_ATTR}]`)?.getAttribute(LINE_UUID_ATTR) ?? null;

    if (selected_line_uuid) {
        const line = lines_orders.find(({ line_uuid }) => line_uuid === selected_line_uuid);
        if (line) {
            const selected_line_order_uuids = line.orders.map(order => order.uuid);
            return arrayIntersection(order_uuids, selected_line_order_uuids);
        }
    }
    return order_uuids;
};

type ZoomDispatch = Dispatch<UpdatePlanningTableWidthAction>;

export type ApplyGanttZoomFromSelectionArgs = {
    source_type: GanttChartSourceTypes;
    dispatch: ZoomDispatch;
};

/** Rubber-band zoom to fit the horizontal extent of the rectangle selection. */
export const applyGanttZoomFromSelection = (selection: Selection, args: ApplyGanttZoomFromSelectionArgs): void => {
    if (!selection) {
        return;
    }

    zoomGanttChartToHorizontalSelection({
        selection_width_px: selection.area_rect.width,
        source_type: args.source_type,
        dispatch: args.dispatch,
        scale_factor: SCALE_FACTOR,
    });

    setTimeout(() => {
        ScrollHandler.scrollToElement(getMiddleElement(selection.selected_els));
    });
};

type MainChartRectangleDispatch = Dispatch<
    ToggleMultiselectOrdersAction | ClearMultiselectOrdersAction | UpdatePlanningTableWidthAction
>;

export type ApplyMultiselectFromSelectionArgs = {
    lines_orders: LineOrders[];
    multiselect_order_uuids?: string[] | null;
    source_type: GanttChartSourceTypes;
    dispatch: MainChartRectangleDispatch;
};

/** Main planning Gantt: after a rectangle gesture, optionally update multiselect */
export const applyMultiselectFromSelection = (selection: Selection, args: ApplyMultiselectFromSelectionArgs): void => {
    const { lines_orders, multiselect_order_uuids, dispatch } = args;

    if (multiselect_order_uuids) {
        dispatch({
            type: CLEAR_MULTISELECT_ORDERS,
            data: undefined,
        });
    }

    if (!selection) {
        dispatch({
            type: TOGGLE_MULTISELECT_ORDERS,
            data: { uuids: [], toggle: false },
        });
        return;
    }

    const { selected_els, first_selected_el } = selection;

    const order_uuids = selected_els
        .map(element => element.getAttribute(ORDER_UUID_ATTR))
        .filter((id): id is string => Boolean(id));

    const normalized_order_uuids = normalizeSelectedLineOrderUuids(first_selected_el, order_uuids, lines_orders);
    dispatch({
        type: TOGGLE_MULTISELECT_ORDERS,
        data: { uuids: normalized_order_uuids, toggle: false },
    });
};
